import React, { useEffect, useRef, useState } from 'react';
import { useStores } from '../../stores';
import { makeStyles } from '@material-ui/core/styles';
import Image from './Image';
import { detectAlpha2Country } from '../../../../utils/country';
import { useTranslation } from 'react-i18next';
import appStyles from '../../../../config/appStyles.js';
import { Link as RLink } from 'react-router-dom';
import { SwipeableDrawer } from '@material-ui/core';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import clsx from 'clsx';
import { countryCodes } from '../../../../stores/Constants';

const useStyles = makeStyles((theme) => appStyles.profileButton(theme));

const ProfileButton = () => {
  const classes = useStyles();
  const [openPopupProfile, setOpenPopupProfile] = useState(false);
  const [isProfileDropdownVisible, setProfileDropdownVisible] = useState(false);
  const { t } = useTranslation(['common']);
  const profilePopupRef = useRef(null);
  const { orderStore, userStore } = useStores();
  const { hasOrders, currentOrder, currentIsModelKit } = orderStore;
  const { user } = userStore;

  const links = [
    {
      label: t('subscription'),
      path: '/myarea/subscription',
      subscriptionRequired: true,
      alignRight: false,
      mobile: true,
    },
    currentIsModelKit && {
      label: t('assembleTutorials'),
      path: '/myarea/video-tutorials',
      subscriptionRequired: true,
      alignRight: false,
      mobile: true,
    },
    currentOrder &&
      !currentIsModelKit && {
        label: currentIsModelKit ? t('model') : t('collection'),
        path: '/myarea/collection',
        subscriptionRequired: true,
        alignRight: false,
        mobile: true,
      },
    {
      label: t('helpCenter'),
      path: '/myarea/help-center',
      subscriptionRequired: false,
      alignRight: false,
      mobile: true,
    },
    {
      label: t('profile'),
      path: '/myarea/profile',
      subscriptionRequired: false,
      alignRight: true,
      mobile: false,
    },
  ].filter(Boolean);

  const handleProfileClick = () => {
    if (!window?.gproxy?.session?.valid()) {
      window.location.href =
        window.location.origin + '/' + detectAlpha2Country() + '/myarea';
    } else {
      setOpenPopupProfile(true);
      setTimeout(() => setProfileDropdownVisible(true), 10);
    }
  };

  const closeProfileDropdown = () => {
    setProfileDropdownVisible(false);
    setTimeout(() => setOpenPopupProfile(false), 300);
  };

  const handleLogout = async () => {
    closeProfileDropdown();
    if (window.snakeAnalytics) {
      window.snakeAnalytics.push([
        'event',
        'logout',
        {
          user: {
            im_user_id: user.UID,
          },
        },
      ]);
    }
    userStore.logout();
  };

  useEffect(() => {
    if (!openPopupProfile) return;
    const handleClickOutside = (event) => {
      if (
        profilePopupRef.current &&
        profilePopupRef.current.contains(event.target)
      )
        return;
      closeProfileDropdown();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPopupProfile]);

  useEffect(() => {
    if (!openPopupProfile) setProfileDropdownVisible(false);
  }, [openPopupProfile]);

  if (!userStore.isLogged) return <span />;

  return (
    <div className="position-relative">
      {userStore.isLogged && (
        <a
          className="d-flex align-items-center"
          onClick={handleProfileClick}
          rel="nofollow">
          <Image className="icon-24 cursor-pointer" name="user-black" />
        </a>
      )}

      {/* Desktop popup */}
      {openPopupProfile && (
        <div
          ref={profilePopupRef}
          className={clsx(classes.profileDropdownBase, {
            profileDropdownVisible: isProfileDropdownVisible,
          })}>
          <div className="d-flex flex-direction-row justify-content-end w-100">
            <div className={clsx(classes.profileArrow, 'w-100 shadow-2')}></div>
          </div>
          <div className="bg-white w-100 shadow-2 text-grey05">
            <List dense className={classes.userAreaList}>
              {links
                .filter((l) => (l.subscriptionRequired ? hasOrders : true))
                .map((l) => (
                  <ListItem className={classes.userAreaItem} key={l.path}>
                    <ListItemText
                      className={classes.listItemText}
                      primary={
                        <RLink
                          onClick={closeProfileDropdown}
                          className={classes.linkMui}
                          to={l.path}>
                          {l.label}
                        </RLink>
                      }
                    />
                  </ListItem>
                ))}
            </List>

            <div className="w-100 bg-grey01">
              <List dense>
                <ListItem>
                  <ListItemText
                    className="d-flex flex-direction-row align-items-center cursor-pointer"
                    onClick={handleLogout}
                    secondary={
                      <span
                        className={clsx(
                          classes.userAreaLinkDesktop,
                          'text-grey05 ml-1',
                        )}>
                        {t('common:logout')}
                      </span>
                    }
                    primary={<Image name="logout_black" className="icon-16" />}
                  />
                </ListItem>
              </List>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Drawer */}
      <SwipeableDrawer
        className={classes.mobileDrawer}
        anchor="right"
        open={openPopupProfile}
        onClose={closeProfileDropdown}
        onOpen={() => setOpenPopupProfile(true)}>
        <div className={clsx(classes.userAreaMobile, 'h-100')}>
          <List className="shadow-1">
            <ListItem>
              <ListItemText
                className="d-flex flex-direction-row justify-content-between align-items-center"
                primary={
                  <span className={classes.userAreaUserName}>
                    {t('common:hiUser', { name: user?.name || '' })}
                  </span>
                }
                secondary={
                  <Image
                    onClick={closeProfileDropdown}
                    className="icon-24 cursor-pointer"
                    name="close"
                  />
                }
              />
            </ListItem>
          </List>

          <List dense className={classes.userAreaList}>
            {links
              .filter(
                (l) => l.mobile && (l.subscriptionRequired ? hasOrders : true),
              )
              .map((l) => (
                <ListItem
                  className={classes.userAreaItem}
                  style={{ padding: '20px' }}
                  key={l.path}>
                  <ListItemText
                    onClick={closeProfileDropdown}
                    className={clsx(
                      classes.listItemText,
                      'd-flex flex-direction-row align-items-center justify-content-between py-2',
                    )}
                    primary={
                      <RLink className={classes.linkMui} to={l.path}>
                        {l.label}
                      </RLink>
                    }
                    secondary={
                      <RLink className={classes.linkMui} to={l.path}>
                        <Image
                          className="icon-16"
                          name="arrow-right-yellow-small"
                        />
                      </RLink>
                    }
                  />
                </ListItem>
              ))}
          </List>
        </div>

        <div className="w-100 bg-grey01">
          <List dense>
            {links
              .filter((n) => n.path === '/myarea/profile')
              .map((link) => (
                <ListItem key={link.path}>
                  <ListItemText
                    className="d-flex flex-direction-row align-items-center cursor-pointer"
                    secondary={
                      <RLink
                        className={clsx(
                          classes.userAreaLinkDesktop,
                          'text-grey05 ml-1',
                        )}
                        onClick={closeProfileDropdown}
                        to={link.path}>
                        {link.label}
                      </RLink>
                    }
                    primary={<Image className="icon-16" name="user-black" />}
                  />
                </ListItem>
              ))}
            <ListItem>
              <ListItemText
                className="d-flex flex-direction-row align-items-center cursor-pointer"
                onClick={handleLogout}
                secondary={
                  <span
                    className={clsx(
                      classes.userAreaLinkDesktop,
                      'text-grey05 ml-1',
                    )}>
                    {t('common:logout')}
                  </span>
                }
                primary={<Image name="logout_black" className="icon-16" />}
              />
            </ListItem>
          </List>
        </div>
      </SwipeableDrawer>
    </div>
  );
};

export default ProfileButton;
